// src/services/geocoding.js
// Free, open-source geocoding (Nominatim) + routing/ETA (OSRM).
// No API key required for either — same family of services already used
// for reverse-geocoding truck positions in services/traccar.js.

// The OSRM demo server's usage policy caps requests at 1/second — this
// serializes EVERY OSRM call from anywhere in the app (route + match)
// through one queue with a guaranteed gap between requests, so multiple
// components calling this around the same time (e.g. Travel History and
// Live Tracking both map-matching a trail) can't collectively exceed that
// limit and get silently throttled/rejected by the server.
let _osrmQueue = Promise.resolve();
function _throttledFetch(url) {
  const run = () => fetch(url).finally(() => new Promise(r => setTimeout(r, 1100)));
  const result = _osrmQueue.then(run, run);
  _osrmQueue = result.catch(() => {});
  return result;
}

/**
 * Search for address suggestions as the user types (autocomplete).
 * Returns an array of { label, address, lat, lng }.
 *
 * Philippine addresses are often written as very long compound strings
 * (house/lot, street, subdivision, barangay, municipality, province) —
 * but small residential subdivision streets are frequently NOT individually
 * mapped in OpenStreetMap's data, even when the subdivision itself is.
 * Searching the whole string at once then returns nothing. To work around
 * that, if the full query has no results, this progressively drops the
 * most specific leading segment (usually the house/street) and retries,
 * so the user still lands close to the right area instead of a dead end —
 * they can then fine-tune the exact spot by clicking directly on the map.
 */
export async function searchAddress(query) {
  if (!query || query.trim().length < 3) return [];

  const segments = query.split(',').map(s => s.trim()).filter(Boolean);
  // Try the full query first, then progressively broader versions
  const attempts = segments.map((_, i) => segments.slice(i).join(', '));

  for (let i = 0; i < attempts.length; i++) {
    const attempt = attempts[i];
    if (attempt.trim().length < 3) continue;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(attempt)}&format=json&addressdetails=1&limit=6&countrycodes=ph`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (!res.ok) continue;
      const data = await res.json();
      if (data.length > 0) {
        return data.map(item => ({
          label: item.display_name,
          address: shortenAddress(item),
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          // Flags a result that came from a broadened search, not the exact
          // typed address, so the UI can let the person know to double-check
          // and fine-tune the pin.
          approximate: i > 0,
        }));
      }
    } catch {
      // try the next, broader attempt
    }
  }
  return [];
}

function shortenAddress(item) {
  const a = item.address || {};
  const parts = [
    a.road || a.suburb || a.neighbourhood,
    a.city || a.town || a.municipality || a.county,
    a.state || a.region,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : item.display_name?.split(',').slice(0, 3).join(',');
}

/**
 * Get driving route + ETA between two points using OSRM's free public server.
 * Returns { distanceKm, durationMinutes, geometry } or null on failure.
 */
export async function getRoute(origin, destination) {
  if (!origin?.lat || !destination?.lat) return null;
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/` +
      `${origin.lng},${origin.lat};${destination.lng},${destination.lat}` +
      `?overview=full&geometries=geojson`;
    const res = await _throttledFetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes?.length) return null;

    const route = data.routes[0];
    return {
      distanceKm: Math.round((route.distance / 1000) * 10) / 10,
      durationMinutes: Math.round(route.duration / 60),
      geometry: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]), // Leaflet wants [lat,lng]
    };
  } catch {
    return null;
  }
}

/**
 * Traces the real road path a truck took, given a sparse sequence of
 * recorded points — NOT a dense continuous GPS trace. This matters:
 * OSRM's Match API (an earlier version of this function used it) is built
 * for traces with a point every few seconds, and fails/returns no result
 * when consecutive points are far apart in time or distance, which is
 * exactly what our data looks like — we only record a point when a
 * truck's status actually transitions (idle<->moving), so two consecutive
 * points can legitimately be many minutes and many kilometers apart.
 *
 * The reliable approach for sparse waypoints like these is chaining real
 * point-to-point driving routes (the same Route API already used for
 * ETA) between each consecutive pair — this works at any distance/time
 * gap, unlike Match, and traces actual roads instead of a straight line.
 *
 * `points` — array of { lat, lng, timestamp } in any order (will be sorted).
 * Returns an array of [lat, lng] pairs tracing the full path, or null if
 * nothing could be traced at all (e.g. fewer than 2 usable points).
 */
export async function matchToRoad(points) {
  const usable = points.filter(p => p.lat != null && p.lng != null);
  if (usable.length < 2) return null;

  const sorted = [...usable].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Collapse consecutive points that haven't meaningfully moved (GPS
  // wobble while idling) before routing between them — avoids pointless
  // near-zero-distance route calls and keeps the path clean.
  const MIN_MOVE_METERS = 40;
  const simplified = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = simplified[simplified.length - 1];
    if (_distanceMeters(last.lat, last.lng, sorted[i].lat, sorted[i].lng) >= MIN_MOVE_METERS) {
      simplified.push(sorted[i]);
    }
  }
  if (simplified.length < 2) return null;

  // Each leg is one sequential, rate-limited request (~1.1s apart) — cap
  // how many waypoints get traced so a very busy day's history doesn't
  // turn into a multi-minute wait. Sampling evenly (not just truncating)
  // keeps the overall shape of a long day's route recognizable even when
  // some points get skipped.
  const MAX_WAYPOINTS = 40;
  let toTrace = simplified;
  if (simplified.length > MAX_WAYPOINTS) {
    const step = (simplified.length - 1) / (MAX_WAYPOINTS - 1);
    toTrace = Array.from({ length: MAX_WAYPOINTS }, (_, i) => simplified[Math.round(i * step)]);
  }

  const fullPath = [];
  for (let i = 0; i < toTrace.length - 1; i++) {
    const a = toTrace[i];
    const b = toTrace[i + 1];
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/` +
        `${a.lng},${a.lat};${b.lng},${b.lat}?overview=full&geometries=geojson`;
      const res = await _throttledFetch(url);
      if (!res.ok) continue;
      const data = await res.json();
      if (data.code !== 'Ok' || !data.routes?.length) continue;
      const segment = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      // Avoid a duplicate point at the seam where this segment's start
      // matches the previous segment's end.
      if (fullPath.length > 0) segment.shift();
      fullPath.push(...segment);
    } catch {
      // If one leg fails, still keep tracing the rest — a straight
      // fallback line between just this pair beats leaving a total gap.
      fullPath.push([a.lat, a.lng], [b.lat, b.lng]);
    }
  }

  return fullPath.length > 1 ? [fullPath] : null;
}

function _distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Format a duration in minutes as a human string, e.g. "2h 15m"
 */
export function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Compute an ETA clock time given a duration in minutes from now (or from a given start time).
 */
export function computeETA(durationMinutes, fromTime = new Date()) {
  if (durationMinutes == null) return null;
  const eta = new Date(fromTime.getTime() + durationMinutes * 60000);
  return eta.toISOString();
}

export function formatETATime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
}
